import * as React from 'react'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import {
  CivilLawCostsOfIncomeType,
  ContractDetailsModel,
  ContractFormType,
  ContractFromContextType,
  ContractType,
  DriverQualificationsType,
  NettoRateType,
  SettlementType,
  TaxCostsType,
} from '../types/contracts'
import { format, startOfYear } from 'date-fns'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useContractService from '../services/ContractService'
import { getContractType } from '@/app/services/contract/contractTypeResolver'
import { getSettlementType } from '@/app/services/contract/settlementTypeResolver'
import {
  getBelgiumDriverQualificationType,
  getCzechDriverQualificationType,
  getDanemarkDriverQualificationType,
  getDriverQualificationType,
} from '@/app/services/contract/driverQualificationTypeResolver'
import {
  QUALIFICATION_TYPES_AUSTRIA,
  QUALIFICATION_TYPES_BELGIUM,
  QUALIFICATION_TYPES_CZECH,
  QUALIFICATION_TYPES_DENMARK,
  QUALIFICATION_TYPES_FINLAND,
  QUALIFICATION_TYPES_FRANCE,
  QUALIFICATION_TYPES_ITALY,
  QUALIFICATION_TYPES_LUKSEMBOURG,
} from '../constants/qualifications'

// Desktop data can hold a value from another country's list (e.g. Italy = 14). Its name is not
// in that country's server enum, so the save failed with 400. Keep such a value as the raw
// number: the API accepts it and the contract keeps it unchanged, like WPF does.
const resolveQualification = (
  raw: unknown,
  resolve: (type: unknown) => string,
  allowed: string[],
): DriverQualificationsType => {
  const mapped = resolve(raw)
  if (typeof raw !== 'number') return mapped as DriverQualificationsType
  if (raw !== -1 && (mapped === DriverQualificationsType.NotSet || !allowed.includes(mapped))) {
    return raw as unknown as DriverQualificationsType
  }
  return mapped as DriverQualificationsType
}

const ContractFormContext = React.createContext<ContractFromContextType | null>(
  null,
)

export const ContractFormProvider = ({
  children,
  companyId,
  driverId,
  contractId,
  isEdit,
  isAnnex,
  lastAnnexDate,
  annexId,
  isFromTemplate,
  templateId
}: {
  children: React.ReactNode
  companyId: string
  driverId?: number
  contractId?: string
  isEdit?: boolean
  isAnnex?: boolean
  lastAnnexDate?: string
  annexId?: string
  isFromTemplate?: boolean
  templateId?: string | undefined
}) => {

  const { t } = useLocalize()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { getContractDetails } = useContractService()
  const [isAnnexState] = React.useState<boolean>(!!isAnnex)
  const [isEditState] = React.useState<boolean>(!!isEdit)
  const [lastAnnexDateState, setLastAnnexDateState] = React.useState<
    string | undefined
  >(undefined)

  const [isReadyToSubmit, setIsReadyToSubmit] = React.useState(false)
  const [formTab, setFormTab] = React.useState<ContractFormType>(
    'contract-parameters',
  )
  const [data, setData] = React.useState<ContractDetailsModel>(
    defaultData as ContractDetailsModel,
  )

  const [sourceData, setSourceData] =
    React.useState<ContractDetailsModel | null>(null)

  const { data: countries } = useQueryCountries({
    companyId: companyId,
  })

  const countriesOptions = React.useMemo(() => {
    return Array.isArray(countries)
      ? countries.map((c) => ({
          label: c.name,
          value: c.id.toString(),
        }))
      : []
  }, [countries])

  const fetchContract = async () => {
    const stopLoading = startLoading()
    try {
      const data = await getContractDetails({
        companyId,
        contractId: annexId || contractId,
        fromTemplate: false,
      })

      const formattedData = {
        ...data,
        id: isEditState ? (isAnnex ? annexId : contractId) : 0,

        isAnnex: isAnnexState,
        startDate: !isEditState && isAnnexState ? undefined : data?.startDate,
        endDate: !isEditState && isAnnexState ? undefined : data?.endDate,
        initialLeaveConfigurationDay: data?.initialLeaveConfigurationDay?.startsWith('0001-01-01')
          ? defaultLeaveConfigurationDay()
          : data?.initialLeaveConfigurationDay,
        type: getContractType(data?.type),

        settlement: getSettlementType(data?.settlement),

        driverQualificationsAustria: resolveQualification(
          data?.driverQualificationsAustria,
          getDriverQualificationType,
          QUALIFICATION_TYPES_AUSTRIA,
        ),
        driverQualificationsBelgium: resolveQualification(
          data?.driverQualificationsBelgium,
          getBelgiumDriverQualificationType,
          QUALIFICATION_TYPES_BELGIUM,
        ),
        driverQualificationsFinland: resolveQualification(
          data?.driverQualificationsFinland,
          getDriverQualificationType,
          QUALIFICATION_TYPES_FINLAND,
        ),
        driverQualificationsFrance: resolveQualification(
          data?.driverQualificationsFrance,
          getDriverQualificationType,
          QUALIFICATION_TYPES_FRANCE,
        ),
        driverQualificationsItaly: resolveQualification(
          data?.driverQualificationsItaly,
          getDriverQualificationType,
          QUALIFICATION_TYPES_ITALY,
        ),
        driverQualificationsLuxembourg: resolveQualification(
          data?.driverQualificationsLuxembourg,
          getDriverQualificationType,
          QUALIFICATION_TYPES_LUKSEMBOURG,
        ),
        driverQualificationsCzech: resolveQualification(
          data?.driverQualificationsCzech,
          getCzechDriverQualificationType,
          QUALIFICATION_TYPES_CZECH,
        ),
        driverQualificationsDenmark: resolveQualification(
          data?.driverQualificationsDenmark,
          getDanemarkDriverQualificationType,
          QUALIFICATION_TYPES_DENMARK,
        ),
      }
      setData(formattedData)
      setSourceData(formattedData)
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  React.useEffect(() => {
    if ((isEdit || isAnnexState || templateId) && contractId) {
      fetchContract()
    }
  }, [isEdit, isAnnexState,templateId])
  React.useEffect(() => {
    if (isAnnexState) {
      setLastAnnexDateState(lastAnnexDate)
    }
  }, [isAnnexState, lastAnnexDate])
  const value = React.useMemo(
    () => ({
      countries,
      countriesOptions,
      setIsReadyToSubmit,
      isReadyToSubmit,
      formTab,
      setFormTab,
      allTabs: getAllTabs(t),
      data,
      sourceData,
      setData,
      isEditState,
      isAnnexState,
      lastAnnexDateState,
      companyId,
      driverId,
    }),
    [
      formTab,
      countriesOptions,
      countries,
      data,
      sourceData,
      isEditState,
      isAnnexState,
      isEditState,
      lastAnnexDateState,
      companyId,
      driverId,
    ],
  )

  return (
    <ContractFormContext.Provider value={value}>
      {children}
    </ContractFormContext.Provider>
  )
}

export const useContractForm = () => {
  const ctx = React.useContext(ContractFormContext)
  if (!ctx)
    throw new Error('useContractForm must be used within ContractFormProvider')
  return ctx
}

const getAllTabs = (t: (key: string) => string): { type: ContractFormType; label: string }[] => [
  { type: 'contract-parameters', label: t('contract.contract-parameters') },
  { type: 'wages', label: t('contract.wages') },
  { type: 'taxes-and-deductions', label: t('contract.taxes-and-deductions') },
  { type: 'lumpscosts', label: t('contract.lump-costs') },
  { type: 'eu-qualifications', label: t('contract.eu-qualifications') },
  { type: 'additional-rates', label: t('contract.additional-rates') },
  { type: 'vacations', label: t('contract.vacations') },
]

const defaultLeaveConfigurationDay = () =>
  format(startOfYear(new Date()), 'YYYY-MM-DD')

const defaultData: Partial<ContractDetailsModel> = {
  type: ContractType.Basic,
  settlement: SettlementType.Month,
  startDate: format(new Date(), 'YYYY-MM-DD'),
  isAnnex: false,
  parentContractId: undefined,

  workTime: {
    numeratorPart: 1,
    denominatorPart: 1,
  },
  weekWorkTimeHours: 39,

  initialLeaveConfigurationDay: defaultLeaveConfigurationDay(),
  workYears: 0,
  daysOfLeave: 0,
  daysOfLeaveAtTheRequest: 0,
  daysOfOverdueLeave: 0,
  daysOfUsedLeave: 0,

  nettoType: NettoRateType.MonthRate,
  contractNettoRate: undefined,
  additionalNettoRate: undefined,

  duty50Rate: 50,
  duty50RateOption: 1,
  duty100Rate: 100,
  duty100RateOption: 1,
  nightHoursRate: 20,
  nightHoursRateOption: 2,
  overtimeAlwaysAs100: false,

  includePit: true,
  includeSickInsurance: false,
  includeSocialInsurance: true,
  isStudent: false,
  bridgePensionFund: false,
  includeTaxFreeAmount: false,
  homelandTaxResidence: true,
  isTaxExempt: false,
  isFamilyFourPlusRelief: false,
  isSeniorRelief: false,
  isTaxOldVersionDisabled: false,
  isSecondThresholdTaxEnabled: false,

  increasedTaxCosts: TaxCostsType.Normal,
  increasedCivilLawCostsOfIncome: CivilLawCostsOfIncomeType.Normal,

  isPpkEnabled: false,
  ppkEmployee: 2,
  ppkEmployer: 1.5,

  isAdditionForLaundryEnabled: false,
  additionForLaundry: undefined,
  additionForLaundryType: undefined,

  isDeductionForMedicalInsuranceEnabled: false,
  deductionForMedicalInsurance: undefined,

  isLifeInsuranceEnabled: false,
  lifeInsuranceValue: 0,
  lifeInsuranceValueWorker: 0,

  isAdditionForBhpEnabled: false,
  additionForBhp: 0,
  additionForBhpType: 1,

  customAdditions: [],

  calculateDutyInCivilLawAgreement: false,
  mapActivityForCivilAgreement: false,
  skipForeignMinimalAmountCalculation: false,
  skipAccommodationForMinimalAmount: false,
  skipAllowanceForMinimalAmount: false,
  personalScheduleMode: false,
  enabledNewOrderSecondEdition: false,
  isNewOrderEnabled: false,

  driverQualificationsAustria:
    DriverQualificationsType.VehiclesTo3Comma5Standard,
  driverQualificationsBelgium: DriverQualificationsType.VhiclesBelow7,
  driverQualificationsCzech: DriverQualificationsType.RegularDriver,
  driverQualificationsFinland: DriverQualificationsType.FinlandBusDriver,
  driverQualificationsFrance: DriverQualificationsType.VehiclesTo3Comma5,
  driverQualificationsItaly: DriverQualificationsType.VehiclesBelow8Group3,
  driverQualificationsLuxembourg: DriverQualificationsType.UnqualifiedDriver,
  driverQualificationsDenmark: DriverQualificationsType.CarriageOfGoods,
}
